import * as React from 'react';

// The puzzle being solved, shown in the best-moves place above the stage tabs
// instead of the theory list or the engine's lines: what to find, where the
// reader is in it, the state, and the ways on: try again, show the solution, back to the game. Drawn from props only; the
// container in `index.tsx` turns the solver's state into these props.

import { toneOf, type PuzzleCardStatus } from './card-tone.ts';
import { dotTitle, type Dot } from './dots.ts';

export { toneOf, type PuzzleCardStatus, type PuzzleCardTone } from './card-tone.ts';

export interface PuzzleCardProps {
  status: PuzzleCardStatus;
  /** "3…" or "Complete the opening". */
  title: string;
  /** "Find the better move", "Mate in 3", "The last move of the Ruy Lopez". */
  prompt: string;
  /** The motif of the mistake the puzzle is made from, after the prompt: "Pin, against the queen"; absent for the opening puzzle or until read. */
  motif?: string;
  /** "move 2 of 3 · try 1", "try 2". */
  progress: string;
  /** The state line: whose move, the engine answering, solved, taking back. */
  text: string;
  /** True for about a second after a correct step of a multi-move puzzle. */
  correct?: boolean;
  /** One dot per puzzle of the game: solved, this one, or still to do. */
  dots?: Dot[];
  /** Once the puzzle is over: "In the game you played Nf3, which cost 2.3 pawns." */
  played?: string;
  onRetry: () => void;
  onStop: () => void;
  /** Plays the correct line on the board by itself. */
  onShow?: () => void;
}

export const PuzzleCard: React.FC<PuzzleCardProps> = ({ status, title, prompt, motif, progress, text, correct, dots, played, onRetry, onStop, onShow }) => {
  const over = status === 'solved' || status === 'shown';
  return (
  <section className={`panel puzzle-card puzzle-card-${status} puzzle-tone-${toneOf(status, correct)}`} aria-live="polite">
    <div className="puzzle-card-head">
      <h2>Puzzle</h2>
      {dots && dots.length > 0 && (
        <ol className="puzzle-dots" aria-label="The puzzles of this game">
          {dots.map((d) => (
            <li key={d.id} className={`puzzle-dot puzzle-dot-${d.state}`} title={dotTitle(d)} aria-current={d.state === 'current' ? 'true' : undefined}>
              <span className="visually-hidden">{dotTitle(d)}</span>
            </li>
          ))}
        </ol>
      )}
      <span className="puzzle-card-progress">{progress}</span>
    </div>
    <p className="puzzle-card-prompt">
      <strong>{title}</strong> {prompt}
      {motif && <span className="puzzle-card-motif"> · {motif}</span>}
    </p>
    <p className="puzzle-card-text" title={over && played ? played : undefined}>
      {over && played ? played : correct && (status === 'playing' || status === 'thinking') ? `Correct. ${text}` : text}
    </p>
    <div className="puzzle-card-actions">
      {(status === 'failed' || status === 'solved' || status === 'shown') && (
        <button type="button" className="btn btn-primary" data-track="review.puzzle.retry" data-track-value={status} onClick={onRetry}>
          {status === 'solved' ? 'Play it again' : 'Try again'}
        </button>
      )}
      {onShow && (status === 'playing' || status === 'thinking' || status === 'failed') && (
        <button type="button" className="btn btn-ghost" data-track="review.puzzle.solution" onClick={onShow} title="Play the correct line on the board">
          Show solution
        </button>
      )}
      <button type="button" className="btn btn-ghost" data-track="review.puzzle.back" onClick={onStop}>
        Back to the game
      </button>
    </div>
  </section>
  );
};
